/*!
  _   _  ___  ____  ___ ________  _   _   _   _ ___
 | | | |/ _ \|  _ \|_ _|__  / _ \| \ | | | | | |_ _|
 | |_| | | | | |_) || |  / / | | |  \| | | | | || |
 |  _  | |_| |  _ < | | / /| |_| | |\  | | |_| || |
 |_| |_|\___/|_| \_\___/____\___/|_| \_|  \___/|___|

=========================================================
* Horizon UI - v1.1.0
=========================================================

* Product Page: https://www.horizon-ui.com/
* Copyright 2023 Horizon UI (https://www.horizon-ui.com/)

* Designed and Coded by Simmmple

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

// Chakra imports
import {
  Avatar,
  Box,
  Flex,
  FormLabel,
  Icon,
  Select,
  Text,
  SimpleGrid,
  useColorModeValue,
  Skeleton,
  SkeletonText,
  SkeletonCircle,
} from "@chakra-ui/react";
// Assets
import Usa from "assets/img/dashboards/usa.png";
// Custom components
import MiniCalendar from "components/calendar/MiniCalendar";
import MiniStatistics from "components/card/MiniStatistics";
import IconBox from "components/icons/IconBox";
import React, { useState, useEffect, useRef } from "react";
import {
  MdAddTask,
  MdAttachMoney,
  MdBarChart,
  MdFileCopy,
} from "react-icons/md";
import CheckTable from "views/admin/default/components/CheckTable";
import ComplexTable from "views/admin/default/components/ComplexTable";
import EndOfDayDataComparison from "views/admin/eodComparison/components/endOfDayComparison";
import { columnsEndOfDayComparison } from "views/admin/eodComparison/variables/columnsData";

import DailyTraffic from "views/admin/default/components/DailyTraffic";
import PieCard from "views/admin/default/components/PieCard";
import Tasks from "views/admin/default/components/Tasks";
import TotalSpent from "views/admin/default/components/TotalSpent";
import WeeklyRevenue from "views/admin/default/components/WeeklyRevenue";
import {
  columnsDataCheck,
  columnsDataComplex,
} from "views/admin/default/variables/columnsData";
import tableDataCheck from "views/admin/default/variables/tableDataCheck.json";
import tableDataComplex from "views/admin/default/variables/tableDataComplex.json";
import useWebSocket, { ReadyState } from 'react-use-websocket';
import ApiService from 'services/api.js';
import AuthService from 'services/auth.services.js';
import { useHistory } from "react-router-dom";

export default function UserReports() {
  // Chakra Color Mode
  const brandColor = useColorModeValue("brand.500", "white");
  const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
  const textColor = useColorModeValue("secondaryGray.900", "white");

  const [socket, setSocket] = useState(null);
  const [totalDataEOD, setTotalDataEOD] = useState([]);
  const [totalDataOK, setTotalDataOK] = useState([]);
  const [totalDataCancel, setTotalDataCancel] = useState([]);
  const [totalDataOKPerMinute, setTotalDataOKPerMinute] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false)
  const history = useHistory();

  //const WS_URL = 'ws://18.141.138.173:1323/stream';

  // old code
  // const connect = () => {

  //   const ws = new WebSocket(ApiService.WS_URL);

  //   ws.onopen = () => {
 //     console.log('Connected to the server');
  //   };

  //   ws.onmessage = (message) => {
  //     setIsLoaded(true)
  //     const parsedData = JSON.parse(message.data);
  //     const totalDataEODParsing = parsedData.totalDataEOD;
  //     const totalDataOKParsing = parsedData.totalDataOK;
  //     const totalDataCancelParsing = parsedData.totalDataCancel;
  //     const totalDataOKPerMinute = parsedData.totalDataOKPerMinute;
  //     setTotalDataEOD(totalDataEODParsing);
  //     setTotalDataOK(totalDataOKParsing);
  //     setTotalDataCancel(totalDataCancelParsing);
  //     setTotalDataOKPerMinute(totalDataOKPerMinute);
  //   };

  //   ws.onclose = () => {
 //     console.log('Disconnected from the server');
  //   };

  //   setSocket(ws);
  // };

  // const disconnect = () => {
  //   if (socket) {
  //     socket.close();
  //   }
  // };

  // useEffect(() => {
  //   connect();

  //   return () => {
  //     disconnect();
  //   };
  // }, []);

  useEffect(() => {
    // console.log('Debug Connecting to:', ApiService.WS_URL);
    // Create a flag to track if component is mounted
    let isMounted = true;

    const ws = new WebSocket(ApiService.WS_URL);

    ws.onopen = () => {
      // console.log('Connected to the server');
    };

    ws.onerror = (error) => {
      console.error('WebSocket Error:', error);
    };

    ws.onmessage = (message) => {
      // Only update state if component is still mounted
      if (isMounted) {
        setIsLoaded(true);
        const parsedData = JSON.parse(message.data);
        setTotalDataEOD(parsedData.totalDataEOD);
        setTotalDataOK(parsedData.totalDataOK);
        setTotalDataCancel(parsedData.totalDataCancel);
        setTotalDataOKPerMinute(parsedData.totalDataOKPerMinute);
      }
    };

    ws.onclose = () => {
      // console.log('Disconnected from the server');
    };

    setSocket(ws);

    // Cleanup function
    return () => {
      isMounted = false; // Mark component as unmounted
      if (ws) {
        ws.close();
      }
    };
  }, []);

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Flex px="25px" justify="space-between" mb="20px" align="center">
        <Text
          color={textColor}
          fontSize="22px"
          fontWeight="700"
          lineHeight="100%"
        >
          Daily Monitoring Data Realtime All Branch
        </Text>
      </Flex>
      <SimpleGrid
        columns={{ base: 1, md: 2, lg: 3, "2xl": 4 }}
        gap='20px'
        mb='20px' mt='20px'>
        <Skeleton height='80px' mb='20px' isLoaded={isLoaded}>
          <MiniStatistics
            startContent={
              <IconBox
                w='56px'
                h='56px'
                bg={boxBg}
                icon={
                  <Icon w='32px' h='32px' as={MdBarChart} color={brandColor} />
                }
              />
            }
            name='Total OK'
            value={totalDataOK}
          />
        </Skeleton>
        <Skeleton height='80px' mb='20px' isLoaded={isLoaded}>
          <MiniStatistics
            startContent={
              <IconBox
                w='56px'
                h='56px'
                bg={boxBg}
                icon={
                  <Icon w='32px' h='32px' as={MdBarChart} color={brandColor} />
                }
              />
            }
            name='Total Cancel'
            value={totalDataCancel}
          />
        </Skeleton>
        <Skeleton height='80px' mb='20px' isLoaded={isLoaded}>
          <MiniStatistics
            startContent={
              <IconBox
                w='56px'
                h='56px'
                bg={boxBg}
                icon={
                  <Icon w='32px' h='32px' as={MdBarChart} color={brandColor} />
                }
              />
            }
            name='Total EOD'
            value={totalDataEOD}
          />
        </Skeleton>
        <Skeleton height='80px' mb='20px' isLoaded={isLoaded}>
          <MiniStatistics
            startContent={
              <IconBox
                w='56px'
                h='56px'
                bg={boxBg}
                icon={
                  <Icon w='32px' h='32px' as={MdBarChart} color={brandColor} />
                }
              />
            }
            name='Total Per Minute (Valid before 21:00)'
            value={totalDataOKPerMinute}
          />
        </Skeleton>
        {/* <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={
                <Icon w='32px' h='32px' as={MdAttachMoney} color={brandColor} />
              }
            />
          }
          name='Spend this month'
          value='$642.39'
        />
        <MiniStatistics growth='+23%' name='Sales' value='$574.34' />
        <MiniStatistics
          endContent={
            <Flex me='-16px' mt='10px'>
              <FormLabel htmlFor='balance'>
                <Avatar src={Usa} />
              </FormLabel>
              <Select
                id='balance'
                variant='mini'
                mt='5px'
                me='0px'
                defaultValue='usd'>
                <option value='usd'>USD</option>
                <option value='eur'>EUR</option>
                <option value='gba'>GBA</option>
              </Select>
            </Flex>
          }
          name='Your balance'
          value='$1,000'
        />
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg='linear-gradient(90deg, #4481EB 0%, #04BEFE 100%)'
              icon={<Icon w='28px' h='28px' as={MdAddTask} color='white' />}
            />
          }
          name='New Tasks'
          value='154'
        />
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={
                <Icon w='32px' h='32px' as={MdFileCopy} color={brandColor} />
              }
            />
          }
          name='Total Projects'
          value='2935'
        /> */}
      </SimpleGrid>

      {/* <SimpleGrid columns={{ base: 1, md: 2, xl: 2 }} gap='20px' mb='20px'>
        <TotalSpent />
        <WeeklyRevenue />
      </SimpleGrid>
      <SimpleGrid columns={{ base: 1, md: 1, xl: 2 }} gap='20px' mb='20px'>
        <CheckTable columnsData={columnsDataCheck} tableData={tableDataCheck} />
        <SimpleGrid columns={{ base: 1, md: 2, xl: 2 }} gap='20px'>
          <DailyTraffic />
          <PieCard />
        </SimpleGrid>
      </SimpleGrid> */}
      <SimpleGrid columns={{ base: 1, md: 1, xl: 1 }} gap='20px' mb='20px'>
        <EndOfDayDataComparison columnsData={columnsEndOfDayComparison} />
        {/* <ComplexTable
          columnsData={columnsDataComplex}
          tableData={tableDataComplex}
        /> */}
        {/* <SimpleGrid columns={{ base: 1, md: 2, xl: 2 }} gap='20px'>
          <Tasks />
          <MiniCalendar h='100%' minW='100%' selectRange={false} />
        </SimpleGrid> */}
      </SimpleGrid>
    </Box>
  );
}
